/**
 * Error Fallback UI Component
 *
 * Displays a user-friendly error message with options to retry or navigate home.
 * Shows stack trace in development mode only.
 *
 * P1 Implementation - Fix 1
 */

import { ErrorOutline, Refresh, Home, ExpandMore, ExpandLess, ContentCopy } from '@/components/ui/qicons';
import React, { ErrorInfo } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Container,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material';
// NOTE: ErrorFallback is rendered by ErrorBoundary OUTSIDE the Router context
// Therefore we cannot use useNavigate() - use window.location instead

interface ErrorFallbackProps {
  /** The error that was caught */
  error: Error | null;
  /** React error info with component stack */
  errorInfo?: ErrorInfo | null;
  /** Called when user clicks "Try Again" */
  onReset?: () => void;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  onReset,
}) => {
  const [showDetails, setShowDetails] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const isDevelopment = import.meta.env.DEV;

  const handleCopyError = async () => {
    const errorText = [
      `Error: ${error?.message}`,
      '',
      'Stack Trace:',
      error?.stack || 'No stack trace available',
      '',
      'Component Stack:',
      errorInfo?.componentStack || 'No component stack available',
    ].join('\n');

    try {
      await navigator.clipboard.writeText(errorText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy error:', err);
    }
  };

  const handleGoHome = () => {
    // Use window.location since ErrorFallback is outside Router context
    window.location.href = '/';
  };

  const handleTryAgain = () => {
    // Reset error state and trigger re-render
    onReset?.();
  };

  return (
    <Container maxWidth="sm">
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mt: 8,
          textAlign: 'center',
          borderTop: 4,
          borderColor: 'error.main',
        }}
      >
        <ErrorOutline
          color="error"
          sx={{ fontSize: 64, mb: 2 }}
        />

        <Typography variant="h5" gutterBottom fontWeight="bold">
          Something went wrong
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {error?.message || 'An unexpected error occurred. Please try again.'}
        </Typography>

        {/* Action buttons */}
        <Box display="flex" gap={2} justifyContent="center" mb={3}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={handleTryAgain}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={handleGoHome}
          >
            Go Home
          </Button>
        </Box>

        {/* Development-only: Error details */}
        {isDevelopment && (error?.stack || errorInfo?.componentStack) && (
          <>
            <Divider sx={{ my: 2 }} />

            <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
              <Button
                size="small"
                startIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? 'Hide' : 'Show'} Error Details
              </Button>
              <IconButton
                size="small"
                onClick={handleCopyError}
                title="Copy error to clipboard"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
              {copied && (
                <Typography variant="caption" color="success.main">
                  Copied!
                </Typography>
              )}
            </Box>

            <Collapse in={showDetails}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mt: 2,
                  textAlign: 'left',
                  maxHeight: 300,
                  overflow: 'auto',
                  bgcolor: 'grey.100',
                }}
              >
                {error?.stack && (
                  <>
                    <Typography
                      variant="caption"
                      color="error"
                      fontWeight="bold"
                      component="div"
                      gutterBottom
                    >
                      Stack Trace:
                    </Typography>
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        mb: 2,
                      }}
                    >
                      {error.stack}
                    </Typography>
                  </>
                )}

                {errorInfo?.componentStack && (
                  <>
                    <Typography
                      variant="caption"
                      color="primary"
                      fontWeight="bold"
                      component="div"
                      gutterBottom
                    >
                      Component Stack:
                    </Typography>
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      }}
                    >
                      {errorInfo.componentStack}
                    </Typography>
                  </>
                )}
              </Paper>
            </Collapse>
          </>
        )}

        {/* Production: Contact support message */}
        {!isDevelopment && (
          <Typography variant="caption" color="text.secondary" display="block" mt={2}>
            If this problem persists, please contact support.
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default ErrorFallback;
